import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useGoogleLogin } from '../../../hooks/useGoogleLogin';
import { useLogout } from '../../../hooks/useLogout';
import { useCurrentUser } from '../../../hooks/useCurrentUser';
import LanguageSelector from '../../../components/LanguageSelector';
import { smoothScrollToElement, smoothScrollToTop } from '../../utils/smoothScroll';
import { UserMenu } from './UserMenu';
import { MobileMenu } from './MobileMenu';
import {
  Nav,
  NavContainer,
  NavLogo,
  LogoIcon,
  NavMenu,
  NavLink,
  NavAuth,
  LoginButton,
  MobileMenuButton,
} from './styles';

const Navbar: React.FC = () => {
  const { t } = useTranslation();
  const { login, loading } = useGoogleLogin();
  const { user } = useCurrentUser();
  const { logout, isLoggingOut } = useLogout();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState<string>('');

  const handleLogoClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    smoothScrollToTop({ duration: 780 });
  };

  const handleNavClick = (event: React.MouseEvent<HTMLAnchorElement>, sectionId: string) => {
    event.preventDefault();
    const target = document.getElementById(sectionId);
    smoothScrollToElement(target);
  };

  const handleMobileNavClick = (event: React.MouseEvent<HTMLAnchorElement>, sectionId: string) => {
    handleNavClick(event, sectionId);
    setMobileMenuOpen(false);
  };

  const handleLogout = async () => {
    try {
      console.log('[DEBUG] Iniciando logout...');
      await logout();
      console.log('[DEBUG] Logout concluído, redirecionando...');
      // O redirecionamento acontecerá automaticamente via App.tsx quando user for null
    } catch (error) {
      console.error('[ERROR] Erro ao fazer logout:', error);
      // Em caso de erro, força redirecionamento
      navigate('/', { replace: true });
    }
  };

  // Detecta scroll para animar topbar
  useEffect(() => {
    const handleScroll = () => {
      const nav = document.querySelector('nav');
      if (!nav) return;

      const currentScrollY = window.scrollY;

      // Adiciona classe 'scrolled' quando passa de 24px
      if (currentScrollY > 24) {
        nav.classList.add('scrolled');
      } else {
        nav.classList.remove('scrolled');
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Detecta qual seção está visível para ativar o link correspondente
  useEffect(() => {
    const sections = [
      'features',
      'integrations',
      'technology',
      'comparison',
      'security',
      'pricing',
      'enterprise',
      'faq'
    ];

    const observerOptions: IntersectionObserverInit = {
      root: null,
      rootMargin: '-40% 0px -55% 0px',
      threshold: 0
    };

    const observerCallback = (entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setActiveSection(entry.target.id);
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);

    sections.forEach((sectionId) => {
      const element = document.getElementById(sectionId);
      if (element) {
        observer.observe(element);
      }
    });

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <>
      <Nav data-navbar="main">
        <NavContainer>
          <NavLogo href="#" onClick={handleLogoClick}>
            <LogoIcon src="/icons/loopra_icon_profile.webp" alt="Loopra" />
            Loopra
          </NavLogo>

          <NavMenu>
            <NavLink href="#features" $active={activeSection === 'features'} onClick={(event) => handleNavClick(event, 'features')}>{t('nav.features')}</NavLink>
            <NavLink href="#integrations" $active={activeSection === 'integrations'} onClick={(event) => handleNavClick(event, 'integrations')}>{t('nav.integrations')}</NavLink>
            <NavLink href="#technology" $active={activeSection === 'technology'} onClick={(event) => handleNavClick(event, 'technology')}>{t('nav.technology')}</NavLink>
            <NavLink href="#comparison" $active={activeSection === 'comparison'} onClick={(event) => handleNavClick(event, 'comparison')}>{t('nav.comparison')}</NavLink>
            <NavLink href="#security" $active={activeSection === 'security'} onClick={(event) => handleNavClick(event, 'security')}>{t('nav.security')}</NavLink>
            <NavLink href="#pricing" $active={activeSection === 'pricing'} onClick={(event) => handleNavClick(event, 'pricing')}>{t('nav.pricing')}</NavLink>
            <NavLink href="#enterprise" $active={activeSection === 'enterprise'} onClick={(event) => handleNavClick(event, 'enterprise')}>{t('nav.enterprise')}</NavLink>
            <NavLink href="#faq" $active={activeSection === 'faq'} onClick={(event) => handleNavClick(event, 'faq')}>{t('nav.faq')}</NavLink>
          </NavMenu>

          <NavAuth>
            <LanguageSelector />
            {!user ? (
              <LoginButton
                onClick={(e) => {
                  e.preventDefault();
                  console.log('[DEBUG] Botão de login clicado');
                  login();
                }}
                disabled={loading}
              >
                <img
                  src="/icons/google.webp"
                  alt="Google"
                  loading="eager"
                  decoding="async"
                  width="16"
                  height="16"
                  style={{
                    width: 16,
                    height: 16,
                    borderRadius: '50%',
                    objectFit: 'cover',
                    flexShrink: 0,
                    verticalAlign: 'middle'
                  }}
                />
                <span className="full-text">
                  {loading ? t('nav.loggingIn') : t('nav.login')}
                </span>
                <span className="short-text">
                  {loading ? t('nav.loggingIn') : t('nav.loginMobile')}
                </span>
              </LoginButton>
            ) : (
              <UserMenu
                user={user}
                isLoggingOut={isLoggingOut}
                onLogout={handleLogout}
              />
            )}

            <MobileMenuButton onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </MobileMenuButton>
          </NavAuth>
        </NavContainer>
      </Nav>

      <MobileMenu
        isOpen={mobileMenuOpen}
        user={user}
        isLoggingOut={isLoggingOut}
        onNavClick={handleMobileNavClick}
        onLogout={handleLogout}
      />
    </>
  );
};

export default Navbar;
